/**
 * A plan price, written in the currency the API says it is billed in.
 *
 * The currency is never assumed here. Plans are priced in every currency we
 * sell in, and the one a brand sees depends on the currency its own store
 * sells in, so a hardcoded symbol would quietly show a euro sign over a dollar
 * amount to every merchant outside the euro zone.
 *
 * Trailing zeros are dropped rather than padded. Our prices are whole numbers,
 * and "189 €" is the figure the pricing page is designed around; "189,00 €"
 * reads as an invoice line rather than a price tag.
 */
export function formatPlanPrice(
  amount: number,
  currencyCode: string,
  locale: string
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // `Intl` throws on a currency code it does not know, and the code arrives
    // over the wire. A pricing page that shows the amount without its symbol is
    // a poor answer; one that crashes on a currency we added server-side before
    // the browser caught up is a worse one.
    return `${amount} ${currencyCode}`;
  }
}
